import React, { useMemo, useState } from 'react';
import PlayerNameButton from '../playerSheet/PlayerNameButton';

type SortConfig = { key: string; direction: 'asc' | 'desc' } | null;

export interface WomensPlayerRow {
  playerId: string;
  playerNumber: string;
  playerName: string;
  position?: string | string[];
  positionLabel: string;
  gamesPlayed: number;
  eightMeterAttempts: number;
  eightMeterSaved: number;
  eightMeterMissed: number;
  eightMeterGoals: number;
  eightMeterConversion: number;
  eightMeterGPG: number;
  eightMeterShotsOnGoal: number;
  eightMeterSaves: number;
  eightMeterSavePct: number;
  eightMeterGoalsAllowed: number;
  eightMeterGAPG: number;
  dcTaken: number;
  dcEarned: number;
  dcTeamWins: number;
  dcTeamWinPct: number;
  dcPerGame: number;
  foulShootingSpace: number;
  foulThreeSecond: number;
  foulDangerousCheck: number;
  yellowCards: number;
  redCards: number;
}

export interface WomensColumn {
  header: string;
  key: keyof WomensPlayerRow;
  render?: (player: WomensPlayerRow) => React.ReactNode;
  sortable?: boolean;
}

export interface WomensTableRenderProps {
  id: 'eightMeter' | 'goalieEightMeter' | 'drawControls' | 'foulsCards';
  title: string;
  columns: WomensColumn[];
  players: WomensPlayerRow[];
  sortConfig: SortConfig;
  onSortChange: (sortConfig: SortConfig) => void;
}

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pct = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const perGame = (value: number, gamesPlayed: number): number =>
  Number((value / (gamesPlayed > 0 ? gamesPlayed : 1)).toFixed(1));

const formatPosition = (position: unknown): string => {
  if (Array.isArray(position)) return position.filter(Boolean).join(' | ') || '-';
  return typeof position === 'string' && position ? position : '-';
};

export const buildWomensPlayerRows = (playersData: any[]): WomensPlayerRow[] =>
  (Array.isArray(playersData) ? playersData : [])
    .map((player) => {
      const gamesPlayed = num(player.gamesPlayed);
      const eightMeterAttempts = num(player.eightMeterAttempts);
      const eightMeterGoals = num(player.eightMeterGoals);
      const eightMeterShotsOnGoal = num(player.eightMeterShotsOnGoal);
      const eightMeterSaves = num(player.eightMeterSaves);
      const eightMeterGoalsAllowed = num(player.eightMeterGoalsAllowed);
      const dcTaken = num(player.dcTaken) || num(player.faceoffwins) + num(player.faceofflosses);
      const dcEarned = num(player.dcEarned);
      const dcTeamWins = num(player.faceoffwins);

      return {
        playerId: String(player.playerSeasonId ?? player.playerId ?? player._id ?? ''),
        playerNumber: String(player.no ?? player.playerNumber ?? ''),
        playerName: `${player.firstName ?? ''} ${player.lastName ?? ''}`.trim() || player.playerName || '-',
        position: player.position,
        positionLabel: formatPosition(player.position),
        gamesPlayed,
        eightMeterAttempts,
        eightMeterSaved: num(player.eightMeterSaved),
        eightMeterMissed: num(player.eightMeterMissed),
        eightMeterGoals,
        eightMeterConversion: pct(eightMeterGoals, eightMeterAttempts),
        eightMeterGPG: perGame(eightMeterGoals, gamesPlayed),
        eightMeterShotsOnGoal,
        eightMeterSaves,
        eightMeterSavePct: pct(eightMeterSaves, eightMeterShotsOnGoal),
        eightMeterGoalsAllowed,
        eightMeterGAPG: perGame(eightMeterGoalsAllowed, gamesPlayed),
        dcTaken,
        dcEarned,
        dcTeamWins,
        dcTeamWinPct: pct(dcTeamWins, dcTaken),
        dcPerGame: perGame(dcEarned, gamesPlayed),
        foulShootingSpace: num(player.foulShootingSpace),
        foulThreeSecond: num(player.foulThreeSecond),
        foulDangerousCheck: num(player.foulDangerousCheck),
        yellowCards: num(player.yellowCards),
        redCards: num(player.redCards),
      };
    })
    .sort((a, b) => (parseInt(a.playerNumber, 10) || 0) - (parseInt(b.playerNumber, 10) || 0));

const percentCell = (value: number) => <div className="text-sm">{value}%</div>;

export type WomensTableLayout = 'split' | 'combined' | 'leaderboard';

const withSheetLink = (
  seasonId: string | null | undefined,
  player: WomensPlayerRow,
  content: React.ReactNode,
) =>
  seasonId && player.playerId ? (
    <PlayerNameButton playerSeasonId={player.playerId} seasonId={seasonId}>
      {content}
    </PlayerNameButton>
  ) : (
    <>{content}</>
  );

const playerColumns = (layout: WomensTableLayout, seasonId?: string | null): WomensColumn[] => {
  if (layout === 'combined') {
    return [
      {
        header: 'Players',
        key: 'playerName',
        sortable: true,
        render: (p) =>
          withSheetLink(
            seasonId,
            p,
            <span>
              <b>{p.playerNumber}</b> {p.playerName}
            </span>,
          ),
      },
    ];
  }
  return layout === 'leaderboard'
    ? [
        {
          header: 'Player',
          key: 'playerName',
          sortable: true,
          render: (p) =>
            withSheetLink(
              seasonId,
              p,
              <div className="text-left font-semibold">
                {p.playerNumber} {p.playerName}
              </div>,
            ),
        },
        { header: 'Position', key: 'positionLabel', sortable: true },
        { header: 'GP', key: 'gamesPlayed', sortable: true, render: (p) => p.gamesPlayed },
      ]
    : [
        { header: 'Player #', key: 'playerNumber', sortable: true },
        {
          header: 'Player Name',
          key: 'playerName',
          sortable: true,
          render: (p) => withSheetLink(seasonId, p, p.playerName),
        },
      ];
};

export const getWomensColumns = (layout: WomensTableLayout, seasonId?: string | null) => {
  const leaderboard = layout === 'leaderboard';
  const base = playerColumns(layout, seasonId);
  const zero = (key: keyof WomensPlayerRow) => (p: WomensPlayerRow) => p[key] as number;

  const eightMeter: WomensColumn[] = [
    ...base,
    { header: 'Attempts', key: 'eightMeterAttempts', sortable: true, render: zero('eightMeterAttempts') },
    { header: 'SS', key: 'eightMeterSaved', sortable: true, render: zero('eightMeterSaved') },
    { header: 'Missed', key: 'eightMeterMissed', sortable: true, render: zero('eightMeterMissed') },
    { header: 'G', key: 'eightMeterGoals', sortable: true, render: zero('eightMeterGoals') },
    {
      header: 'Conv%',
      key: 'eightMeterConversion',
      sortable: true,
      render: (p) => percentCell(p.eightMeterConversion),
    },
    ...(leaderboard
      ? [
          {
            header: '8MGPG',
            key: 'eightMeterGPG',
            sortable: true,
            render: (p) => p.eightMeterGPG.toFixed(1),
          } as WomensColumn,
        ]
      : []),
  ];

  const goalieEightMeter: WomensColumn[] = [
    ...base,
    { header: 'SOG', key: 'eightMeterShotsOnGoal', sortable: true, render: zero('eightMeterShotsOnGoal') },
    { header: 'Saves', key: 'eightMeterSaves', sortable: true, render: zero('eightMeterSaves') },
    { header: 'SV%', key: 'eightMeterSavePct', sortable: true, render: (p) => percentCell(p.eightMeterSavePct) },
    leaderboard
      ? { header: '8MGA', key: 'eightMeterGoalsAllowed', sortable: true, render: zero('eightMeterGoalsAllowed') }
      : { header: 'GA', key: 'eightMeterGoalsAllowed', sortable: true, render: zero('eightMeterGoalsAllowed') },
    ...(leaderboard
      ? [
          {
            header: '8MGAPG',
            key: 'eightMeterGAPG',
            sortable: true,
            render: (p) => p.eightMeterGAPG.toFixed(1),
          } as WomensColumn,
        ]
      : []),
  ];

  const drawControls: WomensColumn[] = [
    ...base,
    { header: 'Taken', key: 'dcTaken', sortable: true, render: zero('dcTaken') },
    { header: 'Earned', key: 'dcEarned', sortable: true, render: zero('dcEarned') },
    { header: 'DC Team Wins', key: 'dcTeamWins', sortable: true, render: zero('dcTeamWins') },
    { header: 'DC Team Win %', key: 'dcTeamWinPct', sortable: true, render: (p) => percentCell(p.dcTeamWinPct) },
    ...(leaderboard
      ? [{ header: 'DC PG', key: 'dcPerGame', sortable: true, render: (p) => p.dcPerGame.toFixed(1) } as WomensColumn]
      : []),
  ];

  const foulsCards: WomensColumn[] = [
    ...base,
    { header: 'Shooting Space', key: 'foulShootingSpace', sortable: true, render: zero('foulShootingSpace') },
    { header: '3 Second', key: 'foulThreeSecond', sortable: true, render: zero('foulThreeSecond') },
    { header: 'Dangerous Check', key: 'foulDangerousCheck', sortable: true, render: zero('foulDangerousCheck') },
    { header: 'Yellow', key: 'yellowCards', sortable: true, render: zero('yellowCards') },
    { header: 'Red', key: 'redCards', sortable: true, render: zero('redCards') },
  ];

  return { eightMeter, goalieEightMeter, drawControls, foulsCards };
};

export const buildWomensTables = (
  playersData: any[],
  layout: WomensTableLayout,
  seasonId?: string | null,
): Omit<WomensTableRenderProps, 'sortConfig' | 'onSortChange'>[] => {
  const rows = buildWomensPlayerRows(playersData);
  const columns = getWomensColumns(layout, seasonId);

  // Rendered directly after the Goalie table: Goalie 8M, Draw Controls, 8M Performance, Fouls & Cards
  return [
    {
      id: 'goalieEightMeter',
      title: 'Goalie 8M',
      columns: columns.goalieEightMeter,
      players: rows.filter((p) => p.eightMeterShotsOnGoal > 0),
    },
    {
      id: 'drawControls',
      title: 'Draw Controls',
      columns: columns.drawControls,
      players: rows.filter((p) => p.dcTaken > 0 || p.dcEarned > 0),
    },
    {
      id: 'eightMeter',
      title: '8M Performance',
      columns: columns.eightMeter,
      players: rows.filter((p) => p.eightMeterAttempts > 0),
    },
    {
      id: 'foulsCards',
      title: 'Fouls & Cards',
      columns: columns.foulsCards,
      players: rows.filter(
        (p) => p.foulShootingSpace + p.foulThreeSecond + p.foulDangerousCheck + p.yellowCards + p.redCards > 0,
      ),
    },
  ];
};

export const buildWomensExportReports = (playersData: any[]) =>
  buildWomensTables(playersData, 'leaderboard').map((table) => ({
    title: table.title,
    data: table.players,
    columns: table.columns,
  }));

interface WomensPlayerTablesProps {
  playersData: any[];
  layout?: WomensTableLayout;
  seasonId?: string | null;
  renderTable: (table: WomensTableRenderProps) => React.ReactNode;
}

const WomensPlayerTables: React.FC<WomensPlayerTablesProps> = ({
  playersData,
  layout = 'split',
  seasonId,
  renderTable,
}) => {
  const [sortConfigs, setSortConfigs] = useState<Record<WomensTableRenderProps['id'], SortConfig>>({
    eightMeter: null,
    goalieEightMeter: null,
    drawControls: null,
    foulsCards: null,
  });

  const tables = useMemo(() => buildWomensTables(playersData, layout, seasonId), [playersData, layout, seasonId]);

  return (
    <>
      {tables.map((table) => (
        <React.Fragment key={table.id}>
          {renderTable({
            ...table,
            sortConfig: sortConfigs[table.id],
            onSortChange: (config) => setSortConfigs((prev) => ({ ...prev, [table.id]: config })),
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export default WomensPlayerTables;
